import type {
  ApiError,
  AssetDetail,
  AssetListResponse,
  AuthTokens,
  CreateAssetResponse,
  AudioAttachResponse,
  MeResponse,
  PlaybackResponse,
  UserListItem,
  UserListResponse,
} from "./types";

const TOKEN_KEY = "helix.accessToken";
const REFRESH_KEY = "helix.refreshToken";

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function saveTokens(tokens: AuthTokens): void {
  localStorage.setItem(TOKEN_KEY, tokens.accessToken);
  localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

async function parseJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { error: { code: "PARSE_ERROR", message: text } } as ApiError;
  }
}

function isApiError(body: unknown): body is ApiError {
  return typeof body === "object" && body !== null && "error" in body;
}

class HttpError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(opts.headers as Record<string, string> | undefined),
  };
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  let res = await fetch(path, { ...opts, headers });
  if (res.status === 401) {
    // Attempt one refresh, then retry once.
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers.Authorization = `Bearer ${getAccessToken()!}`;
      res = await fetch(path, { ...opts, headers });
    }
  }

  const body = await parseJson(res);
  if (!res.ok || isApiError(body)) {
    const err = isApiError(body)
      ? body.error
      : { code: "HTTP_ERROR", message: `HTTP ${res.status}` };
    throw new HttpError(err.code, err.message, res.status);
  }
  return body as T;
}

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    try {
      const res = await fetch("/v1/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const tokens = (await res.json()) as AuthTokens;
      saveTokens(tokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

// ---- Auth ----
export const api = {
  async login(email: string, password: string): Promise<AuthTokens> {
    const res = await fetch("/v1/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const body = await parseJson(res);
    if (!res.ok || isApiError(body)) {
      const err = isApiError(body) ? body.error : { code: "LOGIN_FAILED", message: "Login failed" };
      throw new HttpError(err.code, err.message, res.status);
    }
    return body as AuthTokens;
  },

  async me(): Promise<MeResponse> {
    return request<MeResponse>("/v1/auth/me");
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ ok: true }> {
    return request("/v1/auth/me/password", {
      method: "PATCH",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  async updateProfile(input: {
    displayName?: string | null;
    locale?: string;
    theme?: string;
  }): Promise<MeResponse> {
    return request<MeResponse>("/v1/auth/me/profile", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },

  // ---- Assets ----
  async listAssets(
    limit = 50,
    cursor?: string,
    search?: string,
    filters?: { status?: string; mine?: boolean; tag?: string },
    locale?: string,
  ): Promise<AssetListResponse> {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (cursor) qs.set("cursor", cursor);
    if (search) qs.set("q", search);
    if (filters?.status) qs.set("status", filters.status);
    if (filters?.mine) qs.set("mine", "true");
    if (filters?.tag) qs.set("tag", filters.tag);
    if (locale) qs.set("locale", locale);
    return request<AssetListResponse>(`/v1/assets?${qs.toString()}`);
  },

  async getAsset(id: string, locale?: string): Promise<AssetDetail> {
    const params = new URLSearchParams();
    if (locale) params.set("locale", locale);
    const qs = params.toString();
    return request<AssetDetail>(`/v1/assets/${id}${qs ? `?${qs}` : ""}`);
  },

  async suggestTags(query: string): Promise<string[]> {
    if (!query.trim()) return [];
    const qs = new URLSearchParams({ q: query });
    const res = await request<{ tags: string[] }>(`/v1/assets/tags?${qs.toString()}`);
    return res.tags;
  },

  async createAsset(input: {
    title: string;
    monetization?: string;
    requiredTier?: string;
    profiles?: string[];
    visibility?: string;
    visibleToIds?: string[];
    fileSize?: number;
    multipart?: boolean;
    tags?: string[];
  }): Promise<CreateAssetResponse> {
    return request<CreateAssetResponse>("/v1/assets", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async addTitle(assetId: string, language: string, title: string): Promise<{ language: string; title: string }> {
    return request(`/v1/assets/${assetId}/titles`, {
      method: "POST",
      body: JSON.stringify({ language, title }),
    });
  },

  async ingestAsset(id: string): Promise<{ assetId: string; status: string }> {
    return request(`/v1/assets/${id}/ingest`, { method: "POST" });
  },

  async completeMultipartUpload(
    assetId: string,
    uploadId: string,
    parts: { partNumber: number; etag: string }[],
  ): Promise<{ ok: true }> {
    return request(`/v1/assets/${assetId}/multipart/complete`, {
      method: "POST",
      body: JSON.stringify({ uploadId, parts }),
    });
  },

  async abortMultipartUpload(assetId: string, uploadId: string): Promise<{ ok: true }> {
    return request(`/v1/assets/${assetId}/multipart/abort`, {
      method: "POST",
      body: JSON.stringify({ uploadId }),
    });
  },

  async deleteAsset(id: string): Promise<{ assetId: string; deleted: true }> {
    return request(`/v1/assets/${id}`, { method: "DELETE" });
  },

  async updateAsset(
    id: string,
    body: { visibility?: string; visibleToIds?: string[]; tags?: string[] },
  ): Promise<AssetDetail> {
    return request(`/v1/assets/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  async createAudioAttach(
    id: string,
    input: {
      language: string;
      label?: string;
      channels?: number;
      isDefault?: boolean;
      fileSize?: number;
      multipart?: boolean;
    },
  ): Promise<AudioAttachResponse> {
    return request<AudioAttachResponse>(`/v1/assets/${id}/audio`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async completeAudioMultipartUpload(
    assetId: string,
    audioKey: string,
    uploadId: string,
    parts: { partNumber: number; etag: string }[],
  ): Promise<{ ok: true }> {
    return request(`/v1/assets/${assetId}/audio/multipart/complete`, {
      method: "POST",
      body: JSON.stringify({ audioKey, uploadId, parts }),
    });
  },

  async ingestAudioAttach(
    id: string,
    input: {
      audioKey: string;
      language: string;
      label?: string;
      channels?: number;
      isDefault?: boolean;
    },
  ): Promise<{ assetId: string; status: string }> {
    return request(`/v1/assets/${id}/audio/ingest`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // ---- Users ----
  async listUsers(limit = 50, cursor?: string): Promise<UserListResponse> {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (cursor) qs.set("cursor", cursor);
    return request<UserListResponse>(`/v1/users?${qs.toString()}`);
  },

  async createUser(input: {
    email: string;
    password: string;
    role: string;
  }): Promise<UserListItem> {
    return request<UserListItem>("/v1/users", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async deleteUser(id: string): Promise<{ id: string; deleted: true }> {
    return request(`/v1/users/${id}`, { method: "DELETE" });
  },

  // ---- Playback ----
  async getPlayback(id: string, ttl?: number): Promise<PlaybackResponse> {
    const qs = ttl ? `?ttl=${ttl}` : "";
    return request<PlaybackResponse>(`/v1/playback/${id}${qs}`);
  },

  // ---- Upload ----
  async uploadFile(
    uploadUrl: string,
    file: File,
    onProgress?: (ratio: number) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      // Presigned PUT to MinIO does not require an Authorization header.
      xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(e.loaded / e.total);
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new HttpError("UPLOAD_FAILED", `Upload failed: HTTP ${xhr.status}`, xhr.status));
      };
      xhr.onerror = () => reject(new HttpError("UPLOAD_FAILED", "Network error during upload", 0));
      xhr.send(file);
    });
  },

  async uploadMultipart(
    assetId: string,
    file: File,
    partUrls: { partNumber: number; url: string }[],
    partSize: number,
    uploadId: string,
    onProgress?: (ratio: number) => void,
  ): Promise<void> {
    const parts: { partNumber: number; etag: string }[] = [];
    let uploadedBytes = 0;

    for (const p of partUrls) {
      const start = (p.partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const chunk = file.slice(start, end);

      const etag = await this.uploadPart(p.url, chunk);
      parts.push({ partNumber: p.partNumber, etag });
      uploadedBytes += chunk.size;
      if (onProgress) onProgress(uploadedBytes / file.size);
    }

    await this.completeMultipartUpload(assetId, uploadId, parts);
  },

  /** Like uploadMultipart, but completes against the audio dub endpoint. */
  async uploadAudioMultipart(
    assetId: string,
    audioKey: string,
    file: File,
    partUrls: { partNumber: number; url: string }[],
    partSize: number,
    uploadId: string,
    onProgress?: (ratio: number) => void,
  ): Promise<void> {
    const parts: { partNumber: number; etag: string }[] = [];
    let uploadedBytes = 0;

    for (const p of partUrls) {
      const start = (p.partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const chunk = file.slice(start, end);
      const etag = await this.uploadPart(p.url, chunk);
      parts.push({ partNumber: p.partNumber, etag });
      uploadedBytes += chunk.size;
      if (onProgress) onProgress(uploadedBytes / file.size);
    }

    await this.completeAudioMultipartUpload(assetId, audioKey, uploadId, parts);
  },

  async uploadPart(url: string, chunk: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader("ETag") || "";
          resolve(etag);
        } else {
          reject(new HttpError("UPLOAD_FAILED", `Part upload failed: HTTP ${xhr.status}`, xhr.status));
        }
      };
      xhr.onerror = () => reject(new HttpError("UPLOAD_FAILED", "Network error during part upload", 0));
      xhr.send(chunk);
    });
  },
};

export { HttpError };
